import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const UserAuthContext = createContext();

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth must be used within UserAuthProvider');
  }
  return context;
};

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check for existing token on mount
    const storedToken = localStorage.getItem('userToken');
    const storedUser = localStorage.getItem('userData');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const signup = async (userData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/signup`, userData);
      const { access_token, user: userInfo } = response.data;
      
      setToken(access_token);
      setUser(userInfo);
      localStorage.setItem('userToken', access_token);
      localStorage.setItem('userData', JSON.stringify(userInfo));
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Signup failed'
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/login`, {
        email,
        password
      });
      const { access_token, user: userInfo } = response.data;
      
      setToken(access_token);
      setUser(userInfo);
      localStorage.setItem('userToken', access_token);
      localStorage.setItem('userData', JSON.stringify(userInfo));
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/users/me`,
        profileData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Update failed'
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    signup,
    login,
    logout,
    updateProfile,
    isAuthenticated: !!user && !!token,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};
